import type { BrandingOutput, MeOutput } from '@garage/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiError, api } from './api.js';
import { applyFavicon } from './favicon.js';
import { applyBrand } from './theme.js';

export const ME_QUERY_KEY = ['auth.me'] as const;

/** Utilisateur connecté et garage actif (`null` si non connecté). */
export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async (): Promise<MeOutput | null> => {
      try {
        return await api('auth.me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/**
 * Applique la marque d'un garage (couleurs, titre, favicon) ; `null` : présentation neutre de la
 * plateforme (connexion, choix du garage, console). Vague 5 : la marque appliquée est celle du
 * garage de l'adresse (`/g/:slug/`), y compris en session support, et non celle de `me.active`.
 */
export function useBrand(branding: BrandingOutput | null | undefined, title?: string): void {
  const key = branding ? JSON.stringify(branding) : null;
  useEffect(() => {
    const b = key ? (JSON.parse(key) as BrandingOutput) : null;
    applyBrand(b);
    applyFavicon(b);
    if (!b && title) document.title = title;
  }, [key, title]);
}

/** L'interface ne fait qu'adapter l'affichage : l'API vérifie chaque droit. */
export function hasPermission(me: MeOutput | null | undefined, action: string): boolean {
  return !!me?.active?.permissions.includes(action as never);
}
